import { useEffect, useRef, useState } from 'react';
import { REWARD_TIERS, describeLines, normaliseUkMobile, pickupAvailable, type LineReward, type OrderDto, type CheckoutTotals, rollPickupForward } from '@tikkas/shared';
import { COIN, IC, Svg } from '../art/icons.js';
import { api } from '../lib/api.js';
import { clientTotals, rewardTarget, safePrice, sizeIndexFor, builderFromConfig } from '../lib/bag.js';
import { fmt, num } from '../lib/format.js';
import { CheckoutKey } from '../lib/checkout-key.js';
import { useNav } from '../lib/router.js';
import { featureOn, freePoints, freeSideChoices, orderingOpen, referralName, rememberPayChoice, rememberedPayChoice, rewardPoints, useStore, type PayChoice } from '../lib/store.js';
import { Scroll, TopBar } from '../components/ui.js';
import { LineThumb, pickupText } from '../components/Sheets.js';
import { NewCardForm, WalletButton, WalletMark, payNewCard, paySavedCard, walletUsable, type NewCardHandle, type PayablePending, type Wallets } from '../components/Pay.js';
import { GuestCardForm, payGuestCard, type GuestCardHandle } from '../components/GuestPay.js';
import { guestOrderPath } from '../lib/guest.js';
import { anonId } from '../lib/events.js';
import { track } from '../lib/events.js';
import { testToolsAllowed } from '../lib/platform.js';
import { LegalLinks } from './Legal.js';
import { OrderingClosed } from '../components/ui.js';
import { useLook } from '../lib/look.js';

type Reward = { id: 'side' | 'main' | 'spin'; name: string; points: number; note: string; reward: LineReward };
type Pending = { order: OrderDto; payWith: 'credit' | 'card' | 'wallet'; cancelPath: string; guest?: { token: string } | undefined };

export function Bag() {
  const s = useStore();
  const { navigate, back } = useNav();
  const { t } = useLook();
  const menu = s.menuData?.menu;
  const u = s.me?.customer ?? null;
  const pk = s.config?.stripe.publishableKey ?? '';
  const accountId = s.config?.site.stripeAccountId ?? '';
  const [codeOpen, setCodeOpen] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [rewardsOpen, setRewardsOpen] = useState(false);
  const [serverTotals, setServerTotals] = useState<CheckoutTotals | null>(null);
  const [serverCodeError, setServerCodeError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cardReady, setCardReady] = useState(false);
  /** Stripe's answer on Apple Pay / Google Pay; null until it answers. */
  const [wallets, setWallets] = useState<Wallets | null>(null);
  const touched = useRef(false);
  const [pref] = useState(() => rememberedPayChoice());
  const pending = useRef<Pending | null>(null);
  /** One idempotency key per checkout attempt, kept across retries (lib/checkout-key.ts): a lost answer never places a second order. */
  const attempt = useRef(new CheckoutKey());
  const keyFor = (identity: string): string => attempt.current.get(identity);
  const cardRef = useRef<NewCardHandle>(null);
  // Guest checkout (docs/30): signed out, a first name (required) and a mobile (optional, for the "ready" text) instead of "Sign in to pay".
  const guestOn = !u && featureOn(s, 'guestCheckout') && s.config?.auth.accountsOpen !== false;
  const [guestName, setGuestName] = useState(() => { try { return localStorage.getItem('tikkas-guest-name') ?? ''; } catch { return ''; } });
  const [guestPhone, setGuestPhone] = useState('');
  const [guestCardReady, setGuestCardReady] = useState(false);
  const guestCardRef = useRef<GuestCardHandle>(null);
  const credit = u?.creditPence ?? 0;
  // Group order (docs/28): the bag holds the group's lines as they are; items change on the group page, rewards and codes here.
  const grp = s.groupCheckout;
  const cards = u?.paymentMethods ?? [];
  const cardIds = cards.map((c) => c.id).join(',');
  const fp = freePoints(s);
  const stripeOn = !!pk && !!accountId && !!s.config?.stripe.configured;
  /** A wallet can be chosen while Stripe is still checking (the pay bar shows a placeholder) and once it says yes. */
  // Apple Pay / Google Pay: the Express Checkout Element on the website, Stripe's native sheet in the app (docs/32).
  const walletAllowed = stripeOn;
  const walletMaybe = walletAllowed && (wallets === null || walletUsable(wallets));
  const bagKey = JSON.stringify([s.bag, s.code?.code, s.pickup, s.payWith.kind]);

  // The server's own sums, so what's shown is what's charged.
  useEffect(() => {
    if (!s.bag.length) {
      setServerTotals(null);
      return;
    }
    let live = true;
    setServerTotals(null);
    const t = setTimeout(() => {
      api<{ ok: boolean; totals?: CheckoutTotals; codeError?: string | null; message?: string }>('/api/checkout/quote', { body: { lines: s.bag, code: s.code?.code ?? null, payWith: s.payWith.kind === 'credit' ? 'credit' : 'card' } })
        .then((r) => {
          if (!live) return;
          setServerTotals(r.ok && r.totals ? r.totals : null);
          setServerCodeError(r.codeError ?? (r.ok ? null : r.message ?? null));
        })
        .catch(() => undefined);
    }, 250);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [bagKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Anything changing in the bag drops an unpaid order that was waiting for payment.
  useEffect(() => {
    const p = pending.current;
    if (p) {
      pending.current = null;
      api(p.cancelPath, { body: {} }).catch(() => undefined);
    }
    attempt.current.reset();
    setError(null);
  }, [bagKey]);
  // Signed out: Apple Pay / Google Pay when the device has one, else a card (the choice is kept with the usual preference).
  useEffect(() => {
    if (u || !guestOn) return;
    const cur = s.payWith;
    const ok = cur.kind === 'wallet' ? walletMaybe : cur.kind === 'new';
    if (ok) return;
    s.setPayWith(walletMaybe && !touched.current ? { kind: 'wallet' } : { kind: 'new' });
  }, [u?.id, guestOn, walletMaybe]); // eslint-disable-line react-hooks/exhaustive-deps

  // Which way to pay is selected: what was tapped on this visit if still possible, else the remembered choice, else the saved
  // card, else Apple Pay / Google Pay when the device has one, else a new card.
  useEffect(() => {
    if (!u) return;
    if (u.reviewAccount) {
      if (s.payWith.kind !== 'credit') s.setPayWith({ kind: 'credit' });
      return;
    }
    const valid = (c: PayChoice) => (c.kind === 'saved' ? cards.some((x) => x.id === c.id) : c.kind === 'credit' ? credit > 0 : c.kind === 'wallet' ? walletMaybe : true);
    const cur = s.payWith;
    const first = cards[0];
    const fallback: PayChoice = first ? { kind: 'saved', id: first.id } : walletMaybe ? { kind: 'wallet' } : { kind: 'new' };
    let next: PayChoice;
    if (touched.current && valid(cur)) next = cur;
    else if (pref && pref.kind !== 'new' && valid(pref)) next = pref;
    else if (valid(cur) && cur.kind !== 'new') next = cur;
    else next = fallback;
    if (JSON.stringify(next) !== JSON.stringify(cur)) s.setPayWith(next);
  }, [u?.id, cardIds, credit, walletMaybe]); // eslint-disable-line react-hooks/exhaustive-deps
  const choose = (c: PayChoice) => {
    touched.current = true;
    rememberPayChoice(c);
    s.setPayWith(c);
  };

  if (!menu) return null;
  if (!s.bag.length) {
    return (
      <Scroll>
        <TopBar title={t('page.bag')} />
        <div className="card empty"><Svg html={IC.bag} /><b style={{ fontSize: 17 }}>{t('bag.empty_title')}</b><span className="small muted">{t('bag.empty_body')}</span>
          {u?.vouchers.length ? <span className="welcome" style={{ justifyContent: 'center' }}><Svg html={COIN(16)} /><span>Free food is waiting for you in Offers: {u.vouchers[0]!.kind === 'spin_dessert' ? 'a dessert from the spin' : (menu.items[u.vouchers[0]!.itemId ?? '']?.name ?? referralName(s))}.</span></span> : null}
          <button type="button" className="yellow" style={{ minHeight: 50, padding: '0 24px' }} onClick={() => navigate('/menu', { root: true })}>{t('button.browse')}</button></div>
      </Scroll>
    );
  }
  const useCredit = s.payWith.kind === 'credit';
  const totals = serverTotals ?? clientTotals(menu, s.bag, s.offer, useCredit, credit, u?.points ?? 0);
  const total = totals?.totalPence ?? 0, creditPart = totals?.creditPence ?? 0, cardPart = totals?.cardPence ?? total, earn = totals?.pointsToEarn ?? 0, disc = totals?.discountPence ?? 0, sub = totals?.subtotalPence ?? 0;
  const needsCardForm = !!u && total > 0 && (s.payWith.kind === 'new' || (useCredit && cardPart > 0 && !cards[0]));
  const walletOn = walletUsable(wallets);
  const payer = !!u || guestOn;
  /** The wallet row is chosen: the pay bar shows Stripe's native button (or its placeholder while Stripe is still checking). */
  const walletMode = payer && total > 0 && s.payWith.kind === 'wallet' && walletMaybe;
  const guestCardMode = guestOn && total > 0 && s.payWith.kind === 'new';
  const walletPending = walletMode && wallets === null;

  // Rewards available to this customer
  const rewards: Reward[] = [];
  for (const v of u?.vouchers ?? []) if (v.kind === 'spin_dessert') rewards.push({ id: 'spin', name: 'Free dessert · spin prize', points: 0, note: 'Won on a free spin', reward: { kind: 'spin', prize: 'dessert' } });
  for (const t of s.config?.rewards.tiers ?? REWARD_TIERS) rewards.push({ id: t.id, name: t.name, points: t.points, note: t.id === 'side' ? freeSideChoices(s) : 'Any Regular bowl, wrap or Just Tikkas', reward: { kind: 'points', rewardId: t.id, points: t.points } });
  const isOn = (r: Reward) => s.bag.some((l) => l.config.reward && (r.id === 'spin' ? l.config.reward.kind === 'spin' : l.config.reward.kind === 'points' && l.config.reward.rewardId === r.id));
  const canUse = (r: Reward) => (r.id === 'spin' ? true : fp >= r.points);
  const usable = rewards.filter((r) => canUse(r) && !isOn(r));
  const suggest = usable.find((r) => rewardTarget(menu, s.bag, r.id)) ?? usable[0];
  const sTarget = suggest ? rewardTarget(menu, s.bag, suggest.id) : undefined;
  const applied = s.bag.filter((l) => l.config.reward && l.config.reward.kind !== 'code');
  const useReward = (r: Reward) => {
    const target = rewardTarget(menu, s.bag, r.id);
    if (target) s.applyReward(r.reward, target.id);
    else if (r.id === 'main') s.openSheet({ kind: 'pickMain', reward: r.reward });
    else s.openSheet({ kind: 'pickSide', reward: r.reward, dessertsOnly: r.id === 'spin' });
  };
  const unuse = (r: Reward) => s.unreward(r.id === 'spin' ? 'spin' : 'points', r.id === 'spin' ? undefined : r.id);

  /* ---------- placing and paying ---------- */

  /** One unpaid order per bag: created on the first pay attempt, reused on retries, replaced if the pay route changes. */
  const createPayable = async (payWith: 'credit' | 'card' | 'wallet'): Promise<PayablePending & { order: OrderDto }> => {
    if (s.pickupDays.length && !pickupAvailable(s.pickup, s.pickupDays)) {
      s.openSheet({ kind: 'pickup' });
      throw new Error('Pick a collection time first.');
    }
    const p = pending.current;
    const guestBits = (o: OrderDto, token: string) => ({ base: `/api/guest/orders/${o.id}`, query: `?t=${token}`, returnExtra: `&t=${token}` });
    if (p && p.payWith === payWith && p.order.status === 'pending_payment') return { kind: 'order', id: p.order.id, cardPence: p.order.cardPence, requiresPayment: true, order: p.order, ...(p.guest ? guestBits(p.order, p.guest.token) : {}) };
    if (p) {
      pending.current = null;
      await api(p.cancelPath, { body: {} }).catch(() => undefined);
    }
    if (!u) {
      // A guest (docs/30): their name and mobile go with the order; the server answers with the link token the status page keys off.
      const name = guestName.trim();
      if (!name) throw new Error('Tell us your first name, so we know who to call at the counter.');
      if (guestPhone.trim() && !normaliseUkMobile(guestPhone)) throw new Error('Enter a UK mobile number, for example 07700 900123, or leave it blank.');
      try { localStorage.setItem('tikkas-guest-name', name); } catch { /* private mode */ }
      const g = await api<{ order: OrderDto; token: string; requiresPayment: boolean }>('/api/guest/orders', { body: { clientKey: keyFor(`guest|${payWith}|${name}|${guestPhone.trim()}|${JSON.stringify(s.pickup)}`), lines: s.bag, pickup: s.pickup, payWith: payWith === 'wallet' ? 'wallet' : 'card', note: s.allergyNote, guestName: name, guestPhone: guestPhone.trim() || undefined, deviceId: anonId() } });
      // A key from an attempt long ago whose unpaid order has since timed out: start a fresh order.
      if (g.order.status === 'cancelled') {
        attempt.current.reset();
        return createPayable(payWith);
      }
      pending.current = { order: g.order, payWith, cancelPath: `/api/guest/orders/${g.order.id}/cancel?t=${g.token}`, guest: { token: g.token } };
      return { kind: 'order', id: g.order.id, cardPence: g.order.cardPence, requiresPayment: g.requiresPayment, order: g.order, ...guestBits(g.order, g.token) };
    }
    const r = await api<{ order: OrderDto; requiresPayment: boolean }>('/api/orders', { body: { clientKey: keyFor(`member|${payWith}|${s.code?.code ?? ''}|${s.allergyNote}|${JSON.stringify(s.pickup)}|${grp?.id ?? ''}`), lines: s.bag, pickup: s.pickup, payWith, code: s.code?.code ?? null, note: s.allergyNote, groupId: grp?.id ?? null } });
    if (r.order.status === 'cancelled') {
      attempt.current.reset();
      return createPayable(payWith);
    }
    if (r.requiresPayment) pending.current = { order: r.order, payWith, cancelPath: `/api/orders/${r.order.id}/cancel` };
    return { kind: 'order', id: r.order.id, cardPence: r.order.cardPence, requiresPayment: r.requiresPayment, order: r.order };
  };
  const placed = async (order: OrderDto) => {
    const p = pending.current;
    pending.current = null;
    attempt.current.reset();
    s.clearBag();
    track('order_placed', { orderId: order.id, totalPence: order.totalPence, guest: !u });
    if (p?.guest) {
      // A guest's order lives on this phone by its link (docs/30): Home's live row and the status page read it from there.
      s.addGuestOrder({ id: order.id, token: p.guest.token, number: order.number, readyAt: order.readyAt, name: guestName.trim(), phone: normaliseUkMobile(guestPhone) ?? '', at: new Date().toISOString() });
      s.setFx({ kind: 'placed', order });
      navigate(guestOrderPath(order.id, p.guest.token), { root: true });
      return;
    }
    await s.refreshMe();
    s.setFx({ kind: 'placed', order });
    navigate(`/order/${order.id}`, { root: true });
  };
  /** The big button: credit, the saved card, or the inline new card, in one tap. */
  const pay = async () => {
    if (!u && !guestOn) return navigate('/join?next=/bag');
    if (busy) return;
    // Closed now, or a time that has passed (a bag left from yesterday): ask for a time first instead of failing at the server.
    if (s.pickupDays.length && !pickupAvailable(s.pickup, s.pickupDays)) {
      // A time that has passed rolls forward to the earliest on offer (Saif, 22 Oct); only a closed shop asks for a choice.
      const rolled = rollPickupForward(s.pickup, s.pickupDays);
      if (!pickupAvailable(rolled, s.pickupDays)) {
        s.openSheet({ kind: 'pickup' });
        return s.toast("We're closed right now. Pick a collection time.");
      }
      s.setPickup(rolled);
    }
    setBusy(true);
    setError(null);
    track('checkout_started', { totalPence: total });
    try {
      const p = await createPayable(useCredit ? 'credit' : 'card');
      if (!p.requiresPayment) return await placed(p.order);
      if (!stripeOn) throw new Error(u ? "Card payments aren't switched on here yet. Pay with credit for now." : "Card payments aren't switched on here yet.");
      if (!u) {
        if (!guestCardRef.current) throw new Error('The card form is still loading');
        await payGuestCard(guestCardRef.current, p);
      } else if (s.payWith.kind === 'saved' || (useCredit && cards[0])) {
        await paySavedCard(pk, accountId, p, s.payWith.kind === 'saved' ? s.payWith.id : cards[0]!.id);
      } else {
        if (!cardRef.current) throw new Error('The card form is still loading');
        await payNewCard(pk, accountId, p, await cardRef.current.submit(), s.rememberCard);
      }
      await placed(p.order);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Payment failed');
      setBusy(false);
    }
  };
  const walletPaid = async () => {
    const p = pending.current;
    if (p) await placed(p.order);
    else {
      s.clearBag();
      await s.refreshMe();
      navigate('/', { root: true });
    }
  };
  const edit = (lineId: string) => {
    const l = s.bag.find((x) => x.id === lineId);
    if (!l) return;
    s.setBuilder(builderFromConfig(l.config, l.id, l.qty));
    navigate('/build');
  };
  const payRow = (choice: PayChoice, title: string, sub: string, mark: React.ReactNode) => {
    const checked = JSON.stringify(s.payWith) === JSON.stringify(choice);
    return <button type="button" className="payrow" role="radio" aria-checked={checked} onClick={() => choose(choice)}><span className="radio2" /><span className="grow col" style={{ gap: 2 }}><b>{title}</b>{sub ? <span className="s">{sub}</span> : null}</span>{mark}</button>;
  };
  const savedLabel = s.payWith.kind === 'saved' ? cards.find((c) => s.payWith.kind === 'saved' && c.id === s.payWith.id)?.label ?? 'card' : '';
  const buttonLabel = !u && !guestOn ? 'Sign in to pay' : !u ? `Pay ${fmt(total)}` : total === 0 ? 'Place order · free' : useCredit ? (cardPart > 0 ? `Pay ${fmt(creditPart)} credit + ${fmt(cardPart)} card` : `Pay ${fmt(total)} with credit`) : s.payWith.kind === 'saved' ? `Pay ${fmt(total)} · ${savedLabel}` : `Pay ${fmt(total)}`;
  const buttonDisabled = busy || !totals || (needsCardForm && !cardReady) || (guestCardMode && stripeOn && !guestCardReady);

  return (
    <>
      <Scroll padStyle={{ paddingTop: 16, gap: 26 }}>
        <TopBar title={t('page.bag')} onBack={back} />
        <OrderingClosed />
        {grp && <div className="dbl"><Svg html={IC.people} /><span className="grow">Paying for <b>your group order</b>: {s.bag.reduce((a, l) => a + l.qty, 0)} items from {new Set(s.bag.map((l) => l.config.forName).filter(Boolean)).size || 1} people. Everyone's bag gets their name.</span></div>}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {s.bag.map((l) => {
            const it = menu.items[l.config.itemId];
            const pr = safePrice(menu, l);
            const si = sizeIndexFor(menu, l.config);
            const editable = !grp && !!it && !l.config.membersPrice && (it.hasTikkas || it.sizes.length > 1) && l.config.reward?.kind !== 'code' && l.config.reward?.kind !== 'referral';
            const desc = describeLines(menu, l.config, si);
            return (
              <div className="line2" key={l.id}>
                <LineThumb item={it} config={l.config} />
                <span className="grow col" style={{ gap: 3, minWidth: 0 }}>
                  <button type="button" className="ltext" onClick={() => editable && edit(l.id)} tabIndex={editable ? 0 : -1}>
                    <b>{pr ? `${it && it.sizes.length > 1 ? `${pr.sizeName} ` : ''}${pr.name}` : it?.name ?? 'Unavailable item'}</b>
                    {desc.length ? <span>{desc.map((d, i) => <span key={i}>{i ? <br /> : null}{d}</span>)}</span> : null}
                    {l.config.forName ? <span className="small muted">For {l.config.forName}</span> : null}
                    {!pr && <span className="err">Not available right now. Remove it to carry on.</span>}
                  </button>
                  {grp ? null : <span className="lrow"><span className="mini2"><button type="button" aria-label={l.qty === 1 ? 'Remove' : 'Fewer'} onClick={() => { if (l.qty === 1) s.toast(`${it?.name ?? 'Item'} removed`); s.lineQty(l.id, -1); }}>{l.qty === 1 ? <Svg html={IC.bin} /> : '−'}</button><span>{l.qty}</span><button type="button" aria-label="More" disabled={!!l.config.reward || !!l.config.membersPrice || l.qty >= 20} onClick={() => s.lineQty(l.id, 1)}>+</button></span>{editable ? <button type="button" className="ledit" onClick={() => edit(l.id)}>Edit</button> : null}</span>}
                </span>
                <span className="lprice">{pr ? (pr.linePence === 0 ? 'Free' : fmt(pr.linePence)) : '—'}</span>
              </div>
            );
          })}
          {grp ? <button type="button" className="addmore" onClick={() => navigate(`/g/${grp.token}`)}><Svg html={IC.people} /> Your group order · change what's in it</button> : <button type="button" className="addmore" onClick={() => navigate('/menu', { root: true })}><span>+</span> Add more items</button>}
        </div>
        <section className="col" style={{ gap: 10 }}><h2 className="h3">Pickup</h2>
          <button type="button" className="card pickline" style={{ background: 'var(--surface)', padding: '14px 16px' }} onClick={() => s.openSheet({ kind: 'pickup' })}><span><Svg html={IC.clock} /></span><span className="grow col" style={{ gap: 1, minWidth: 0 }}><b>{pickupText(s)}</b><span className="s">{s.config?.site.address}</span></span><span className="chg">Change</span></button>
          <textarea className="note-in" style={{ minHeight: 56 }} placeholder="Allergies or a note for the kitchen (optional)" value={s.allergyNote} maxLength={300} onChange={(e) => s.setAllergyNote(e.target.value)} aria-label="Allergies or a note for the kitchen" />
          <span className="small muted">Common allergens are handled in our kitchen, so traces may be present. For a serious allergy, speak to the team when you collect.</span>
        </section>
        <section className="col" style={{ gap: 10 }}><h2 className="h3">Rewards and codes</h2>
          <div className="card rows extras">
            <div className={`r ${rewardsOpen ? 'open' : ''}`} style={rewardsOpen ? { gap: 6 } : undefined}>
              <div className="row" style={{ gap: 12, width: '100%' }}>
                <button type="button" className="rowbtn" aria-expanded={rewardsOpen} onClick={() => (u ? setRewardsOpen(!rewardsOpen) : navigate('/join?next=/bag'))}>
                  <span className="ic"><Svg html={COIN(22)} /></span>
                  <span className="grow col" style={{ gap: 2, minWidth: 0 }}><b>{!u ? 'Rewards Club' : applied.length ? 'Reward applied' : suggest && !rewardsOpen ? suggest.name : 'Rewards'}</b>
                    <span className="s">{!u ? 'Sign in to use your points' : applied.length ? applied.map((l) => menu.items[l.config.itemId]?.name).join(', ') : suggest && !rewardsOpen ? `${suggest.points ? `${num(suggest.points)} points` : suggest.note} · See all` : `${num(fp)} points${fp < rewardPoints(s).side ? ` · ${num(rewardPoints(s).side - fp)} more for a free side` : ''}`}</span></span>
                  {suggest && !rewardsOpen && !applied.length ? null : <span style={{ transform: `rotate(${rewardsOpen ? 90 : 0}deg)`, transition: 'transform .2s', display: 'flex' }}><Svg html={IC.chev} /></span>}
                </button>
                {u && suggest && !rewardsOpen && !applied.length ? <button type="button" className="pillbtn line" onClick={() => useReward(suggest)}>{sTarget ? 'Apply' : 'Use'}</button> : null}
              </div>
              {rewardsOpen && (
                <div style={{ paddingLeft: 50 }}>
                  {rewards.map((r) => {
                    const on = isOn(r);
                    return <div className="rw row" style={{ gap: 10 }} key={r.id}><span className="grow col" style={{ gap: 2 }}><b style={{ fontSize: 15 }}>{r.name}</b><span className="small muted">{r.points ? `${num(r.points)} points` : r.note}</span></span>
                      {on ? <button type="button" className="pillbtn" onClick={() => unuse(r)}>Remove</button> : canUse(r) ? <button type="button" className="pillbtn line" onClick={() => useReward(r)}>Use</button> : <span className="small muted tnum togo">{num(r.points - fp)} to go</span>}</div>;
                  })}
                </div>
              )}
            </div>
            <div className={`r ${codeOpen && !s.code ? 'open' : ''}`}>
              {s.code ? (
                <><span className="ic"><Svg html={IC.gift} /></span><span className="grow col" style={{ gap: 2 }}><b>{s.code.label}</b><span className={`s ${serverCodeError ? 'err' : 'good'}`}>{serverCodeError ?? `Code ${s.code.code} · ${s.code.offer.type === 'amount_off' && !disc ? 'add a main to use it' : 'applied'}`}</span></span><button type="button" className="pillbtn" onClick={() => { s.removeCode(); setCodeInput(''); }}>Remove</button></>
              ) : codeOpen ? (
                <div className="col" style={{ gap: 8, width: '100%' }}><h2 className="h3">Got a code?</h2>
                  <form className="codebox" onSubmit={async (e) => { e.preventDefault(); if (await s.applyCode(codeInput)) setCodeOpen(false); }}><input type="text" autoComplete="off" placeholder="e.g. ACME5" aria-label="Code" value={codeInput} onChange={(e) => setCodeInput(e.target.value)} autoFocus /><button type="submit">Apply</button></form>
                  {s.codeError ? <span className="err">{s.codeError}</span> : <span className="small muted">From a friend, your office or a creator{testToolsAllowed(s.config?.testTools) ? ' · staging codes: ACME5, SARAHEATS' : ''}</span>}</div>
              ) : (
                <button type="button" className="rowbtn" onClick={() => setCodeOpen(true)}><span className="ic"><Svg html={IC.gift} /></span><span className="grow col" style={{ gap: 2 }}><b>Got a code?</b><span className="s">From a friend, your office or a creator</span></span><Svg html={IC.chev} /></button>
              )}
            </div>
          </div>
        </section>
        {guestOn && total > 0 && orderingOpen(s) && (
          <section className="col" style={{ gap: 10 }} aria-label="Your details"><h2 className="h3">Your details</h2>
            <div className="card" style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label className="col" style={{ gap: 6, fontSize: 13, fontWeight: 600 }} htmlFor="gname">First name
                <input id="gname" className="txt-in" type="text" placeholder="So we know who to call at the counter" autoComplete="given-name" maxLength={40} value={guestName} onChange={(e) => setGuestName(e.target.value)} /></label>
              <label className="col" style={{ gap: 6, fontSize: 13, fontWeight: 600 }} htmlFor="gphone">Mobile (optional)
                <span className="phonein"><span>+44</span><input id="gphone" className="txt-in" type="tel" inputMode="tel" placeholder="7700 900123" autoComplete="tel" value={guestPhone} onChange={(e) => setGuestPhone(e.target.value)} /></span></label>
              <span className="small muted">Only for a text when your order's ready. Never for marketing, and no code to type.</span>
            </div>
            <p className="small muted" style={{ margin: 0 }}>Already a member? <button type="button" className="link" style={{ display: 'inline', padding: 0, font: 'inherit', fontWeight: 700 }} onClick={() => navigate('/join?next=/bag')}>Sign in</button> to use your points and credit.</p>
          </section>
        )}
        {guestOn && total > 0 && orderingOpen(s) && (
          <section className="col" style={{ gap: 10 }}><h2 className="h3">Pay with</h2>
            <div className="card rows payrows" role="radiogroup" aria-label="Pay with">
              {walletOn && wallets && payRow({ kind: 'wallet' }, wallets.applePay ? (wallets.googlePay ? 'Apple Pay or Google Pay' : 'Apple Pay') : 'Google Pay', '', <span className="row" style={{ gap: 6 }}>{wallets.applePay && <WalletMark wallet="apple" />}{wallets.googlePay && <WalletMark wallet="google" />}</span>)}
              {walletPending && <div className="payrow skel" aria-hidden="true"><span className="radio2" /><span className="grow col"><span className="skel-bar" /></span></div>}
              {payRow({ kind: 'new' }, 'Card', 'Visa, Mastercard, Amex', <span className="paymark card">CARD</span>)}
            </div>
            {guestCardMode && stripeOn && <div className="card" style={{ padding: '12px 14px' }}><GuestCardForm ref={guestCardRef} pk={pk} accountId={accountId} amountPence={total} onReady={() => setGuestCardReady(true)} /></div>}
            {guestCardMode && !stripeOn && <p className="err">Card payments aren't switched on here yet.</p>}
            <LegalLinks lead="Paying means you agree to our" docs={['terms', 'privacy']} />
          </section>
        )}
        {u && total > 0 && orderingOpen(s) && (
          <section className="col" style={{ gap: 10 }}><h2 className="h3">Pay with</h2>
            <div className="card rows payrows" role="radiogroup" aria-label="Pay with">
              {walletOn && wallets && payRow({ kind: 'wallet' }, wallets.applePay ? (wallets.googlePay ? 'Apple Pay or Google Pay' : 'Apple Pay') : 'Google Pay', '', <span className="row" style={{ gap: 6 }}>{wallets.applePay && <WalletMark wallet="apple" />}{wallets.googlePay && <WalletMark wallet="google" />}</span>)}
              {walletPending && <div className="payrow skel" aria-hidden="true"><span className="radio2" /><span className="grow col"><span className="skel-bar" /></span></div>}
              {!u.reviewAccount && cards.map((c) => payRow({ kind: 'saved', id: c.id }, c.label, c.expMonth ? `Expires ${String(c.expMonth).padStart(2, '0')}/${String(c.expYear).slice(-2)}` : 'Saved card', <span className="paymark visa">{(c.brand ?? 'card').toUpperCase()}</span>))}
              {credit > 0 && payRow({ kind: 'credit' }, 'Tikkas credit', `${fmt(credit)} available${credit < total ? ` · the rest on ${cards[0] ? cards[0].label : 'a card'}` : ''}`, <span className="badge">2× points</span>)}
              {!u.reviewAccount && payRow({ kind: 'new' }, cards.length ? 'Another card' : 'Card', 'Visa, Mastercard, Amex', <span className="paymark card">CARD</span>)}
            </div>
            {u.reviewAccount && <p className="small muted" style={{ margin: 0 }}>Demo account: orders are paid with the store credit already on it, so no card is ever charged. The shop knows to ignore them.</p>}
            {needsCardForm && stripeOn && <div className="card" style={{ padding: '12px 14px' }}><NewCardForm ref={cardRef} pk={pk} remember={s.rememberCard} onRemember={s.setRememberCard} onReady={() => setCardReady(true)} reveal={touched.current} /></div>}
            {needsCardForm && !stripeOn && <p className="err">Card payments aren't switched on here yet. Pay with credit for now.</p>}
            <LegalLinks lead="Paying means you agree to our" docs={useCredit ? ['terms', 'credit', 'rewards'] : ['terms', 'rewards']} />
          </section>
        )}
      </Scroll>
      <div className="footer" style={{ gap: 6 }}>
        {disc > 0 && s.code && <div className="subline"><span>Subtotal {fmt(sub)}</span><span className="good">{s.code.code} −{fmt(disc)}</span></div>}
        <div className="totalrow"><span>Total</span><b>{fmt(total)}</b></div>
        <div className="subline"><span><Svg html={COIN(14)} /> +{num(earn)} points{useCredit && creditPart > 0 ? ' · 2× with credit' : ''}{!u ? ' if you join' : ''}</span></div>
        {error && <p className="err" role="alert" style={{ margin: 0 }}>{error}</p>}
        {payer && total > 0 && walletAllowed && orderingOpen(s) && <WalletButton pk={pk} accountId={accountId} amountPence={total} selected={walletMode} createPayable={() => { track('checkout_started', { totalPence: total }); return createPayable('wallet'); }} onPaid={walletPaid} onError={setError} onAvailable={setWallets} onBusy={setBusy} />}
        {!walletMode && orderingOpen(s) && <button type="button" className="yellow" style={{ marginTop: 6 }} disabled={buttonDisabled} onClick={pay}>{busy ? 'One moment…' : buttonLabel}</button>}
      </div>
    </>
  );
}
